import type { components } from '@fluencelabs/vodopad-admin-api'
import { Breadcrumb, Card, Descriptions, Typography } from 'antd'
import { useParams } from 'react-router-dom'
import { Link } from 'react-router-dom'

import { ClusterNameLink } from '@/components/common/ClusterNameLink'
import { FormattedDate } from '@/components/common/FormattedDate'
import { MONO_STYLE } from '@/components/common/MonoId'
import { RecordFallback } from '@/components/common/RecordFallback'
import { StatusTag } from '@/components/common/StatusTag'
import { TroubleshootCard } from '@/components/common/TroubleshootCard'
import { UserEmailLink } from '@/components/common/UserEmailLink'
import { useAdminRecord } from '@/hooks/useAdminRecord'

type UserSubnetDto = components['schemas']['UserSubnetDto']

export default function SubnetShowPage() {
  const { id: subnetId } = useParams<{ id: string }>()
  const { record: subnet, query: subnetQuery } = useAdminRecord<UserSubnetDto>(
    'subnets',
    subnetId,
  )

  if (!subnetId) return null
  if (!subnet)
    return (
      <RecordFallback
        label="Subnet"
        isLoading={subnetQuery.isLoading}
        error={subnetQuery.error}
      />
    )

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/subnets">Subnets</Link> },
          { title: subnet.name },
        ]}
      />
      <h2 style={{ marginBottom: 24 }}>
        Subnet: <Typography.Text copyable>{subnet.name}</Typography.Text>
      </h2>

      <div style={{ display: 'grid', gap: 24 }}>
        <Card>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="ID">
              <Typography.Text copyable style={MONO_STYLE}>
                {subnet.id}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Name">
              <Typography.Text copyable>{subnet.name}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <StatusTag status={subnet.status} />
            </Descriptions.Item>
            <Descriptions.Item label="IPv4 CIDR">
              {subnet.ipv4Cidr ? (
                <Typography.Text copyable>{subnet.ipv4Cidr}</Typography.Text>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="IPv6 CIDR">
              {subnet.ipv6Cidr ? (
                <Typography.Text copyable>{subnet.ipv6Cidr}</Typography.Text>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="User">
              <UserEmailLink userId={subnet.userId} />
            </Descriptions.Item>
            <Descriptions.Item label="Cluster">
              <ClusterNameLink clusterId={subnet.clusterId} />
            </Descriptions.Item>
            <Descriptions.Item label="VPC">
              <Link
                to={`/vpcs/${subnet.vpcId}`}
                style={{
                  textDecoration: 'underline',
                  textDecorationStyle: 'dotted',
                  textUnderlineOffset: 4,
                }}
              >
                <Typography.Text copyable={{ text: subnet.vpcId }}>
                  {subnet.vpcId}
                </Typography.Text>
              </Link>
            </Descriptions.Item>
            <Descriptions.Item label="Ready Since">
              {subnet.readySince ? (
                <FormattedDate date={subnet.readySince} fmt="PPp" />
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Removed">
              <FormattedDate date={subnet.removedAt} fmt="PPp" />
            </Descriptions.Item>
          </Descriptions>
        </Card>
        <TroubleshootCard
          kind="subnet"
          userId={subnet.userId}
          resourceId={subnet.id}
        />
      </div>
    </div>
  )
}
